import React, { FC } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { scale } from '@components/frame';
import { ColorFunction } from '@styles/colors/types';
import { useManageActiveMachineAnimatedIcon } from '@v2/pages/machines/modules/now/components/Overview/components/MachineActivityBlock/hooks/useManageActiveMachineAnimatedIcon';
import { ACTIVE_MACHINE_ICON_CONFIG, ACTIVE_MACHINE_ICON_ANIMATION_CONFIG } from '@v2/pages/machines/modules/now/components/Overview/constants';
import { WithColor } from '@styles/colors';

interface ActiveMachineAnimatedIconProps extends WithColor {
  color: string;
  size: number;
}

const ActiveMachineAnimatedIcon: FC<ActiveMachineAnimatedIconProps> = ({ C, color, size }) => {
  const { animatedCenterDotStyle } = useManageActiveMachineAnimatedIcon(ACTIVE_MACHINE_ICON_ANIMATION_CONFIG);

  // Calculate proportional sizes based on the provided size vs original 60
  const ratio = size / ACTIVE_MACHINE_ICON_CONFIG.outerSize;

  const outerBorderWidth = scale(ACTIVE_MACHINE_ICON_CONFIG.outerBorderWidth * ratio);
  const innerBgSize = scale(ACTIVE_MACHINE_ICON_CONFIG.innerBgRadius * 2 * ratio);
  const targetOuterSize = scale(ACTIVE_MACHINE_ICON_CONFIG.targetOuterRadius * 2 * ratio);
  const targetOuterBorderWidth = scale(ACTIVE_MACHINE_ICON_CONFIG.targetOuterBorderWidth * ratio);
  const targetInnerSize = scale(ACTIVE_MACHINE_ICON_CONFIG.targetInnerRadius * 2 * ratio);
  const targetInnerBorderWidth = scale(ACTIVE_MACHINE_ICON_CONFIG.targetInnerBorderWidth * ratio);
  const scaledSize = scale(size);

  return (
    <View
      style={{
        width: scaledSize,
        height: scaledSize,
        borderRadius: scaledSize / 2,
        borderWidth: outerBorderWidth,
        borderColor: C(color),
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Inner background circle with opacity */}
      <View
        style={{
          position: 'absolute',
          width: innerBgSize,
          height: innerBgSize,
          borderRadius: innerBgSize / 2,
          backgroundColor: C(color),
          opacity: ACTIVE_MACHINE_ICON_CONFIG.innerBgOpacity,
        }}
      />

      {/* Target outer ring */}
      <View
        style={{
          width: targetOuterSize,
          height: targetOuterSize,
          borderRadius: targetOuterSize / 2,
          borderWidth: targetOuterBorderWidth,
          borderColor: C(color),
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Target inner ring (animated) */}
        <Animated.View
          style={[
            {
              width: targetInnerSize,
              height: targetInnerSize,
              borderRadius: targetInnerSize / 2,
              borderWidth: targetInnerBorderWidth,
              borderColor: C(color),
              backgroundColor: C(color),
            },
            animatedCenterDotStyle,
          ]}
        />
      </View>
    </View>
  );
};

export { ActiveMachineAnimatedIcon };
